/** @internal */
const responseContentTypeIsExpected = (reqHeaders, resHeaders) => {
    const accept = reqHeaders.get('Accept');
    if (!accept || accept.indexOf('*/') !== -1) {
        return true;
    }
    const expected = accept
        .split(/, */)
        .map((type) => type.replace(/[;*].*/, ''));
    const contentType = resHeaders.get('Content-Type');
    return contentType && expected.some((exp) => contentType.indexOf(exp) === 0);
};
/**
 * ```js
 * import Api from '@op/opds.api-base' // class
 * ```
 *
 * An OP-specific wrapper for
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch fetch()}
 */
export class Api {
    /** @internal */
    static defaultErrorHandler(response, { hasExpectedType }) {
        const error = new Error(hasExpectedType
            ? `Response with HTTP status ${response.status}`
            : 'Response data has unexpected type');
        error.response = response;
        return Promise.reject(error);
    }
    /**
     * @param root - Base URL for all requests
     * @param options - Optional options object
     */
    constructor(root, options) {
        /** @internal */
        this.handlers = {
            error: Api.defaultErrorHandler,
            request: [],
            response: []
        };
        const { acceptLanguages, csrfHeaderSelector, csrfTokenSelector, fetchInit, headers, onError, pathParams, timeout, wrapMethod } = options || {};
        this.csrfHeaderSelector = csrfHeaderSelector || "meta[name='_csrf_header']";
        this.csrfTokenSelector =
            csrfTokenSelector !== undefined
                ? csrfTokenSelector
                : "meta[name='_csrf']";
        this.defaultHeaders = new Headers();
        if (acceptLanguages) {
            const locales = acceptLanguages.slice();
            let accept = locales.shift();
            let q = 0.9;
            for (const lc of locales) {
                accept += `, ${lc};q=${q}`;
                q -= 0.1;
            }
            this.defaultHeaders.set('Accept-Language', accept || '*');
        }
        if (headers) {
            for (const [name, header] of Object.entries(headers)) {
                this.defaultHeaders.set(name, header);
            }
        }
        this.defaultInit = Object.assign({ cache: 'default', credentials: 'include', redirect: 'follow' }, fetchInit);
        this.defaultPathParams = Object.assign({}, pathParams);
        this.defaultTimeout = timeout || 0;
        if (typeof onError === 'function') {
            this.handlers.error = onError;
        }
        this.root = root || '';
        this.wrapMethod = !!wrapMethod;
    }
    /**
     * Register a request or response event handler.
     *
     * Request handlers will be called with the `(url, init)` arguments that
     * the wrapped `fetch()` will be called with; returning a `string` will
     * set the `url` value.
     *
     * Response handlers will be called with the `Response`, and should also
     * return a `Response` to effect changes.
     */
    on(event, handler) {
        switch (event) {
            case 'request':
                this.handlers.request.push(handler);
                break;
            case 'response':
                this.handlers.response.push(handler);
                break;
            /* istanbul ignore next */
            default:
                throw new Error(`Unknown event type ${JSON.stringify(event)}`);
        }
    }
    /**
     * Unregister an event handler.
     *
     * @returns `true` if the handler was found and removed
     */
    off(event, handler) {
        const evHandlers = this.handlers[event];
        /* istanbul ignore if */
        if (!evHandlers) {
            throw new Error(`Unknown event type ${JSON.stringify(event)}`);
        }
        const idx = evHandlers.indexOf(handler);
        /* istanbul ignore if */
        if (idx < 0) {
            return false;
        }
        evHandlers.splice(idx, 1);
        return true;
    }
    /** @internal */
    buildUrl(path, { pathParams, query }) {
        let url = this.root + path;
        const params = Object.assign({}, this.defaultPathParams, pathParams);
        for (const [key, param] of Object.entries(params)) {
            url = url.replace(`{${key}}`, String(param));
        } // only replaces first instance
        if (query) {
            const parts = [];
            for (const [key, value] of Object.entries(query)) {
                if (value !== undefined) {
                    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
                }
            }
            /* istanbul ignore else */
            if (parts.length > 0) {
                url += `?${parts.join('&')}`;
            }
        }
        return url;
    }
    /** @internal */
    buildInit(method, { body, headers: customHeaders, init: customInit, timeout }) {
        const headers = new Headers(this.defaultHeaders);
        if (body && !(body instanceof FormData)) {
            body = this.stringifyBodyObjectIfNeeded(body);
            this.setContentTypeApplicationJsonIfNeeded(headers);
        }
        if ((this.wrapMethod ||
            window.__RD_PROXY__) &&
            method !== 'GET' &&
            method !== 'POST') {
            headers.set('X-HTTP-Method', method);
            method = 'POST';
        }
        if (this.csrfTokenSelector) {
            this.setCsrfTokenHeader(headers);
        }
        if (customHeaders) {
            for (const [name, header] of Object.entries(customHeaders)) {
                headers.set(name, header);
            }
        }
        const init = Object.assign({ body, headers, method }, this.defaultInit, customInit);
        if (timeout == null) {
            timeout = this.defaultTimeout;
        }
        if (timeout > 0 && typeof AbortController !== 'undefined') {
            const controller = new AbortController();
            init.signal = controller.signal;
            setTimeout(() => controller.abort(), timeout);
        }
        return init;
    }
    setContentTypeApplicationJsonIfNeeded(headers) {
        if (!headers.has('Content-Type')) {
            headers.set('Content-Type', 'application/json');
        }
    }
    stringifyBodyObjectIfNeeded(body) {
        if (typeof body === 'object') {
            body = JSON.stringify(body);
        }
        return body;
    }
    setCsrfTokenHeader(headers) {
        const csrfNode = document.querySelector(this.csrfTokenSelector);
        if (csrfNode) {
            const token = csrfNode.getAttribute('content');
            const csrfHeader = document.querySelector(this.csrfHeaderSelector);
            const header = csrfHeader && csrfHeader.getAttribute('content');
            /* istanbul ignore else */
            if (token && header) {
                headers.set(header, token);
            }
        }
    }
    /**
     * A generic wrapper for `window.fetch()` to use with OP REST API calls
     *
     * If `config.body` is a non-null non-FormData object, it will be stringified
     * using `JSON.stringify()`, and the request's `Content-Type` header will be
     * set to `application/json` if not set manually.
     *
     * If the `Content-Type` of a successful response starts with `application/json`
     * and `config.rawResponse` is not set, the returned promise will resolve to
     * the JSON-parsed body of the response.
     *
     * Only on network error will the returned promise reject with something
     * other than an {@link ApiError}.
     *
     * See also: {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API Fetch API}
     *
     * @param method - The HTTP request method, e.g. `"GET"` or `"POST"`
     * @param path - The part of the URL to append to the Api root; should start with /
     * @param config - Optional configuration
     */
    fetch(method, path, config = {}) {
        let url = this.buildUrl(path, config);
        const init = this.buildInit(method, config);
        for (const handler of this.handlers.request) {
            const res = handler(url, init);
            /* istanbul ignore else */
            if (typeof res === 'string') {
                url = res;
            }
        }
        let request = window.fetch(url, init);
        request = this.workaroundForMissingAbortController(config, request);
        return request
            .then((response) => this.useResponseHandlers(response))
            .then((response) => {
            const isEmpty = response.headers.get('Content-Length') === '0';
            const hasExpectedType = responseContentTypeIsExpected(init.headers, response.headers);
            const typeHeader = response.headers.get('Content-Type');
            const isJson = typeHeader && typeHeader.indexOf('application/json') === 0;
            if (response.ok && hasExpectedType) {
                if (config.rawResponse || !isJson) {
                    return response;
                }
                if (isEmpty) {
                    return null;
                }
                return response.json().catch((error) => {
                    error.response = response;
                    throw error;
                });
            }
            return this.handlers.error(response, {
                hasExpectedType,
                isEmpty,
                isJson
            });
        });
    }
    useResponseHandlers(response) {
        for (const handler of this.handlers.response) {
            const res = handler(response);
            /* istanbul ignore else */
            if (res instanceof Response) {
                response = res;
            }
        }
        return response;
    }
    // This is a temporary patch, required until whatwg-fetch and node-fetch
    // both release support for polyfilling AbortController
    workaroundForMissingAbortController(config, request) {
        const timeout = config.timeout != null ? config.timeout : this.defaultTimeout;
        if (timeout > 0 && typeof AbortController === 'undefined') {
            const origRequest = request;
            request = new Promise((resolve, reject) => {
                setTimeout(() => reject(new Error('timeout')), timeout);
                origRequest.then(resolve, reject);
            });
        }
        return request;
    }
    /** Convenience wrapper for `Api#fetch('GET', ...)` */
    get(path, config) {
        return this.fetch('GET', path, config);
    }
    /** Convenience wrapper for `Api#fetch('POST', ...)` */
    post(path, config) {
        return this.fetch('POST', path, config);
    }
    /** Convenience wrapper for `Api#fetch('PUT', ...)` */
    put(path, config) {
        return this.fetch('PUT', path, config);
    }
    /** Convenience wrapper for `Api#fetch('DELETE', ...)` */
    delete(path, config) {
        return this.fetch('DELETE', path, config);
    }
}
